/**
 * SEModal is a skin of the react-modal project. There's a good chance that
 * if you have questions about this modal's props, behavior, or capabilities beyond
 * what is covered in this component's propTypes declaration, react-modal's
 * documentation (http://reactcommunity.org/react-modal/) will provide the answers
 * */

import React from "react";
import Modal from "react-modal";
import { STREETEASY_SITE_CONTENT_SELECTOR } from "@streeteasy/design-system/constants/streeteasySiteContentSelector";
import {
  bool,
  func,
  node,
  number,
  objectOf,
  oneOfType,
  shape,
  string
} from "prop-types";

const defaultDialogStyles = {
  background: "#FFFFFF",
  boxShadow: "0 2px 4px 0 rgba(0, 0, 0, 0.08)",
  boxSizing: "border-box",
  border: "none",
  borderRadius: "unset",
  bottom: "unset",
  left: "unset",
  outline: "none",
  overflow: "auto",
  padding: "unset",
  position: "absolute",
  right: "unset",
  top: "unset",
  WebkitOverflowScrolling: "touch"
};

const defaultOverlayStyles = {
  backgroundColor: "rgba(0, 0, 0, 0.22)",
  bottom: 0,
  boxSizing: "border-box",
  display: "flex",
  height: "100vh",
  justifyContent: "center",
  left: 0,
  overflow: "auto",
  position: "fixed",
  right: 0,
  top: 0,
  width: "100vw"
};

export const isAcceptableRootElement = rootElementId => {
  const rootElements = rootElementId.split(", ");
  const rejectedElements = ["html", "body"];

  const isRejected = element => {
    const result = rejectedElements.includes(element);

    if (result) {
      // eslint-disable-next-line no-console
      console.warn(
        `Using "${element}" as a "rootElementId" prevents screen readers from functioning properly, and is therefore not an acceptable root element for SEModal. Reverting to default values.`
      );
    }

    return result;
  };

  return !rootElements.some(isRejected);
};

const isRunningInJest = () => process.env.JEST_WORKER_ID !== undefined;

const SEModal = ({
  ariaLabel,
  dialogStyles,
  handleOpen,
  overlayStyles,
  rootElementId,
  zIndex,
  ...passThroughProps
}) => {
  // No DOM in jest snapshots, so cannot set root element
  if (process.env.STORYBOOK_SNAPSHOTS !== "true" && !isRunningInJest()) {
    if (rootElementId && isAcceptableRootElement(rootElementId)) {
      Modal.setAppElement(rootElementId);
    }
  }

  const onAfterOpen = args => {
    // prevent scrolling of main page while modal is open
    document.documentElement.style.overflow = "hidden";
    handleOpen(args);
  };

  const onAfterClose = () => {
    document.documentElement.style.overflow = "";
  };

  return (
    <Modal
      ariaHideApp={!isRunningInJest()} // This is needed only for Jest. It should not be set otherwise
      contentLabel={ariaLabel}
      onAfterClose={onAfterClose}
      onAfterOpen={onAfterOpen}
      style={{
        content: {
          ...defaultDialogStyles,
          ...dialogStyles
        },
        overlay: {
          ...defaultOverlayStyles,
          zIndex,
          ...overlayStyles
        }
      }}
      {...passThroughProps}
    />
  );
};

SEModal.defaultProps = {
  aria: {},
  ariaLabel: "",
  className: "",
  dialogStyles: {},
  handleOpen: () => {},
  id: "",
  overlayStyles: {},
  rootElementId: STREETEASY_SITE_CONTENT_SELECTOR,
  zIndex: 1000
};

SEModal.propTypes = {
  /**
   * If the modal is labeled with visible text (like a header),
   *  you should specify the element including the label with the aria-labelledby
   *  field of the "aria" prop. If there is a lengthier description of the modal's
   *  content, it should be specified using the "describedby" field.
   *
   *  Note: If the modal does not have text describing its content, please
   *  use the ariaLabel prop to provide a description.
   * */
  aria: shape({
    describedby: string,
    labelledby: string
  }),
  /**
   * Used to provide a description for the modal's contents if there is no
   * header/label or description. If your modal does have a header, please use
   * the aria prop described above.
   * */
  ariaLabel: string,
  children: node.isRequired,
  className: string,
  /**
   * CSS styles for the content dialog generated by react-modal. Useful for:
   * - setting width of dialog as a percentage of the viewport
   * - adding a margin between the dialog and boundaries of the viewport
   * */
  dialogStyles: objectOf(string),
  /**
   * Function that will be run after the modal has opened
   * */
  handleOpen: func,
  id: string,
  isOpen: bool.isRequired,
  /**
   * Function called when:
   * - User clicks on overlay surrounding the dialog
   * - User presses the escape key
   *
   * Often used for managing modal's isOpen prop
   * */
  onRequestClose: func.isRequired,
  overlayStyles: objectOf(string),
  /**
   * From the react-modal docs:
   *
   * "It is important for users of screenreaders that other page content be
   *  hidden (via the aria-hidden attribute)
   *  while the modal is open"
   *
   * To facilitate this, we  pass a rootElementId prop that references elements
   * we want to hide from screen readers.
   *
   * We provide a convenient default value for this prop that references a
   * container element present in all StreetEasy layout files. Please do not
   * pass a value to rootElementId unless you have a specific reason: for example,
   * rendering the modal in a test environment where StreetEasy-specific
   * page elements are absent.
   */
  rootElementId: string,
  /**
   * Optional value for the z-index of the modal, defaults to 1000.
   */
  zIndex: oneOfType([number, string])
};

export default SEModal;
